import { useEffect, useState } from "react";
import { useGetCompanyByIdQuery } from "../../../API/authApi";
import { Template, useTheme, createTemplate } from "../../../Context/ThemeContext";
import { VisitorPageContents } from "./visitorPage";
import { useParams } from "react-router";
import { useSearchParams } from "react-router-dom";

const useVisitorPageHooks = () => {
    const { id } = useParams<{ id: string, }>();
    const [searchParams] = useSearchParams();
    const rawTab = searchParams.get('tab');
    const isValidTab = (value: any): value is VisitorPageContents => {
        return Object.values(VisitorPageContents).includes(value);
    };
    const tab: VisitorPageContents = isValidTab(rawTab) ? rawTab : VisitorPageContents.MENU;
    const isNewVisitor = localStorage.getItem(`${id}_visit`) ? false : true;
    const { data: businessData, isLoading: requestLoading, isSuccess } = useGetCompanyByIdQuery({ id: +id!, new_visitor: isNewVisitor });
    const [isLoading, setIsLoding] = useState<boolean>(true);
    const { setTemplate, setCustomTemplate, getTemplate } = useTheme();
    const loading = isLoading || requestLoading;

    // Validation function for template colors
    const validateTemplateColors = (template: any): { isValid: boolean; errors: string[]; validatedTemplate: any } => {
        const errors: string[] = [];
        const validatedTemplate: { light: { [key: string]: string }; dark: { [key: string]: string } } = {
            light: {},
            dark: {}
        };

        // Get default template for fallback
        const defaultTemplate = getTemplate("default") || { light: {}, dark: {} };

        // Required color fields
        const requiredFields = [
            '--background-color', '--title', '--title-font', '--text-color', '--text-dark',
            '--input-label', '--shadow-color', '--box-shadow', '--box-shadow-inset',
            '--card-bg', '--input-bg', '--primary-button', '--primary-hover',
            '--disable-button', '--icon-color', '--footer-bg'
        ];
        const optionalSmartFields = [
            '--smart-subtitle-color',
            '--smart-subtitle-font'
        ];

        // Validate each required field
        requiredFields.forEach(field => {
            const lightValue = template.light?.[field];
            const darkValue = template.dark?.[field];

            // Check if field exists
            if (!lightValue || !darkValue) {
                errors.push(`Missing ${field} in template`);
                // Use default values
                validatedTemplate.light[field] = defaultTemplate.light?.[field] || getDefaultValue(field);
                validatedTemplate.dark[field] = defaultTemplate.dark?.[field] || getDefaultValue(field);
                return;
            }

            // Basic validation - check if values are strings and not empty
            if (typeof lightValue !== 'string' || typeof darkValue !== 'string' ||
                lightValue.trim() === '' || darkValue.trim() === '') {
                errors.push(`Invalid ${field} values`);
                validatedTemplate.light[field] = defaultTemplate.light?.[field] || getDefaultValue(field);
                validatedTemplate.dark[field] = defaultTemplate.dark?.[field] || getDefaultValue(field);
            } else {
                validatedTemplate.light[field] = lightValue;
                validatedTemplate.dark[field] = darkValue;
            }
        });

        // Optional smart-title fields: preserve when present, fallback silently when absent.
        optionalSmartFields.forEach(field => {
            const lightValue = template.light?.[field];
            const darkValue = template.dark?.[field];

            if (typeof lightValue === 'string' && lightValue.trim() !== '') {
                validatedTemplate.light[field] = lightValue;
            } else {
                validatedTemplate.light[field] = defaultTemplate.light?.[field] || getDefaultValue(field);
            }

            if (typeof darkValue === 'string' && darkValue.trim() !== '') {
                validatedTemplate.dark[field] = darkValue;
            } else {
                validatedTemplate.dark[field] = defaultTemplate.dark?.[field] || getDefaultValue(field);
            }
        });

        return {
            isValid: errors.length === 0,
            errors,
            validatedTemplate
        };
    };

    // Get default value for a field
    const getDefaultValue = (field: string): string => {
        const defaults: { [key: string]: string } = {
            '--background-color': '#ffffff',
            '--title': '#344054',
            '--title-font': 'Montserrat arm',
            '--smart-subtitle-color': '#344054',
            '--smart-subtitle-font': 'Montserrat arm',
            '--text-color': '#505053',
            '--text-dark': 'rgb(36, 36, 36)',
            '--input-label': 'rgb(24, 55, 79)',
            '--shadow-color': 'rgba(0, 0, 0, 0.3)',
            '--box-shadow': '0px 4px 8px rgba(0, 0, 0, 0.3)',
            '--box-shadow-inset': '0px 2px 6px rgba(0, 0, 0, 0.2), inset 0px 4px 6px rgba(0, 0, 0, 0.15)',
            '--card-bg': 'linear-gradient(135deg, #ffffff, #f8f8f8)',
            '--input-bg': '#e4e2e2',
            '--primary-button': '#ee163a',
            '--primary-hover': 'rgb(170, 0, 43)',
            '--disable-button': '#c1c2c4',
            '--icon-color': '#344054',
            '--footer-bg': 'rgba(250,250,250,0.75)'
        };
        return defaults[field] || '#000000';
    };

    useEffect(() => {
        if (businessData) {
            // Get the actual template_id from businessData
            const template = String(businessData?.styles);

            try {
                // Try to parse template_id as JSON
                const parsedTemplate = JSON.parse(template);

                // Check if it has the required structure for template colors
                if (parsedTemplate && typeof parsedTemplate === 'object' &&
                    parsedTemplate.light && parsedTemplate.dark) {

                    console.log('🎨 Parsed template from business data:', parsedTemplate);

                    // Validate the template before using it
                    const validationResult = validateTemplateColors(parsedTemplate);

                    if (validationResult.isValid) {
                        console.log('✅ Template validation passed');
                        console.log('🎨 Light mode colors:', parsedTemplate.light);
                        console.log('🎨 Dark mode colors:', parsedTemplate.dark);

                        // Create a custom template with the validated colors
                        const customTemplate = createTemplate(
                            `custom-${id}`, // Unique name for this business
                            validationResult.validatedTemplate.light,
                            validationResult.validatedTemplate.dark
                        );

                        // Set the custom template
                        setCustomTemplate(`custom-${id}`, customTemplate);
                        setTemplate(`custom-${id}`);

                        console.log('✅ Custom template applied:', `custom-${id}`);
                    } else {
                        console.warn('❌ Template validation failed:', validationResult.errors);
                        console.log('🔄 Using default template instead');
                        setTemplate("default");
                    }
                } else {
                    // If JSON doesn't have the right structure, use default
                    console.log('❌ Invalid template structure, using default');
                    setTemplate("default");
                }
            } catch (error) {
                // If parsing fails, treat template_id as a regular template name
                console.log('⚠️ Template ID is not JSON, treating as template name:', template);

                // Check if it's a valid template name (you can add more validation here)
                if (template && template !== "null" && template !== "undefined") {
                    setTemplate(template as Template);
                } else {
                    setTemplate("default");
                }
            }

            localStorage.setItem(`${id}_visit`, 'true');
            setIsLoding(false);
        }
        return () => setTemplate("default");
    }, [setTemplate, setCustomTemplate, businessData, id]);


    return {
        tab,
        loading,
        isSuccess,
        businessData,
    }

};

export default useVisitorPageHooks